import * as THREE from 'three/webgpu';
import { attribute, vec3 } from 'three/tsl';

import { RENDER_ORDER } from './materials.js';
import { plateY } from '../build/prims.js';

/**
 * @file The tracer pool, extracted from `systems/Gameplay.js` so BOTH gameplay
 * modes (hero and squad) can draw the same "you shot that one" line without one
 * importing the other.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SHOT IS INSTANT; THE BULLET IS NOT
 * ─────────────────────────────────────────────────────────────────────────────
 * Damage is hitscan and already applied by the time `fire()` is called — there
 * is no projectile actor and there is deliberately no projectile COLLISION.
 * What travels here is a pure visual: a short streak that flies muzzle -> impact
 * at ~160 m/s, so a 9 m shot is over in ~55 ms and a 30 m squad exchange in
 * ~190 ms. The whole point is legibility, not simulation: a full-length ribbon
 * that blinks on and off (what this was) tells you a shot happened, whereas a
 * streak that MOVES tells you which way it went and which of the six things in
 * front of you it was aimed at.
 *
 * The streak is the segment between the head (`speed * t`) and a `trail` metres
 * behind it, both clamped to the flight path — so it grows out of the muzzle,
 * flies at fixed length, and is swallowed by the impact point rather than
 * popping off. Total on-screen time is `(len + trail) / speed`, capped by
 * `life` so a pathologically long shot can never linger.
 *
 * One `InstancedMesh` over a flat XZ quad (`plateY`), lying at the flight's
 * interpolated height and yawed along the shot. Flat rather than billboarded on
 * purpose: at the game's 52-degree pitch a ground-parallel ribbon reads as a
 * beam and needs no camera data at all. Alpha rides an `iAlpha` instanced
 * attribute, and the colour is deliberately above 1 so the bloom threshold
 * catches it.
 *
 * The matrices are rebuilt on the CPU every frame for LIVE tracers only — the
 * pools are 16 (hero) and 48 (squad) slots, so that is a rounding error against
 * the blob shadows, which rewrite every actor every frame.
 *
 * @module engine/render/Tracers
 */

/**
 * @param {number} max Pool size (simultaneous tracers).
 * @param {THREE.Object3D} parent Identity-transform parent (level root).
 * @param {Object} [o]
 * @param {number} [o.life=0.5] HARD CAP in seconds. The flight normally ends
 *   long before this; it exists so a bad endpoint cannot strand a slot.
 * @param {number} [o.width=0.075] Ribbon width, metres.
 * @param {number} [o.speed=160] Bullet speed, m/s. Fast enough to read as
 *   instant, slow enough that the eye catches the direction.
 * @param {number} [o.trail=2.6] Streak length, metres.
 * @returns {{mesh: THREE.InstancedMesh, fire: Function, update: Function, dispose: Function}}
 */
export function createTracerPool(max, parent, o = {}) {
  const { life: lifeCap = 0.5, width = 0.075, speed = 160, trail = 2.6 } = o;
  const geo = plateY(1, 1);
  const alpha = new THREE.InstancedBufferAttribute(new Float32Array(max), 1);
  alpha.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('iAlpha', alpha);
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

  const mat = new THREE.MeshBasicNodeMaterial();
  mat.name = 'tracer';
  mat.transparent = true;
  mat.depthWrite = false;
  mat.fog = false;
  mat.vertexColors = false;
  mat.side = THREE.DoubleSide;
  mat.colorNode = vec3(2.4, 2.2, 1.9);
  mat.opacityNode = attribute('iAlpha', 'float');

  const mesh = new THREE.InstancedMesh(geo, mat, max);
  mesh.name = 'tracers';
  mesh.count = max;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.instanceMatrix.array.fill(0);
  mesh.renderOrder = RENDER_ORDER.emissive;
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();
  mesh.visible = false;
  parent.add(mesh);

  // Per-slot flight. `age < 0` means the slot is idle.
  const age = new Float32Array(max).fill(-1);
  const ox = new Float32Array(max);
  const oy = new Float32Array(max);
  const oz = new Float32Array(max);
  // Unit direction + length, so the per-frame update is two multiply-adds.
  const dx = new Float32Array(max);
  const dy = new Float32Array(max);
  const dz = new Float32Array(max);
  const len = new Float32Array(max);
  let cursor = 0;
  let live = 0;

  /**
   * Writes slot `i`'s quad for a streak spanning `[a, b]` metres along its
   * flight path. A zero-length span writes a degenerate quad, which rasterises
   * nothing — that is how an idle slot stays invisible without a branch.
   * @param {number} i @param {number} a @param {number} b @param {number} al
   */
  const write = (i, a, b, al) => {
    const m = mesh.instanceMatrix.array;
    const o16 = i * 16;
    const l = b - a;
    if (!(l > 1e-4)) {
      for (let k = 0; k < 16; k++) m[o16 + k] = 0;
      alpha.array[i] = 0;
      return;
    }
    // Yaw from the XZ direction; local +Z runs along the shot, local +X across.
    const hx = dx[i];
    const hz = dz[i];
    const hl = Math.hypot(hx, hz);
    const c = hl > 1e-6 ? hz / hl : 1;
    const s = hl > 1e-6 ? hx / hl : 0;
    const mid = (a + b) * 0.5;
    m[o16] = c * width;
    m[o16 + 1] = 0;
    m[o16 + 2] = -s * width;
    m[o16 + 3] = 0;
    m[o16 + 4] = 0;
    m[o16 + 5] = 1;
    m[o16 + 6] = 0;
    m[o16 + 7] = 0;
    m[o16 + 8] = s * l;
    m[o16 + 9] = 0;
    m[o16 + 10] = c * l;
    m[o16 + 11] = 0;
    m[o16 + 12] = ox[i] + dx[i] * mid;
    m[o16 + 13] = oy[i] + dy[i] * mid;
    m[o16 + 14] = oz[i] + dz[i] * mid;
    m[o16 + 15] = 1;
    alpha.array[i] = al < 0 ? 0 : al > 1 ? 1 : al;
  };

  return {
    mesh,
    /**
     * Launches a streak. The hit has ALREADY happened — this is feedback.
     *
     * @param {number} x0 @param {number} y0 @param {number} z0 Muzzle.
     * @param {number} x1 @param {number} y1 @param {number} z1 IMPACT point.
     *   On a miss (`systems/accuracy.js`) this is the scatter point, not the
     *   target — which is the entire reason a miss is visible.
     */
    fire(x0, y0, z0, x1, y1, z1) {
      const vx = x1 - x0;
      const vy = y1 - y0;
      const vz = z1 - z0;
      const l = Math.hypot(vx, vy, vz);
      if (l < 1e-4) return;
      const i = cursor;
      cursor = (cursor + 1) % max;
      if (age[i] < 0) live++;
      ox[i] = x0;
      oy[i] = y0;
      oz[i] = z0;
      dx[i] = vx / l;
      dy[i] = vy / l;
      dz[i] = vz / l;
      len[i] = l;
      age[i] = 0;
      write(i, 0, 0, 0);
    },
    /** @param {number} dt */
    update(dt) {
      if (live <= 0) {
        if (mesh.visible) mesh.visible = false;
        return;
      }
      live = 0;
      for (let i = 0; i < max; i++) {
        if (age[i] < 0) continue;
        const t = (age[i] += dt);
        const head = t * speed;
        const tail = head - trail;
        const l = len[i];
        // Done when the TAIL clears the impact point, not when the head lands —
        // otherwise the streak vanishes a frame after it appears on a short shot.
        if (tail >= l || t >= lifeCap) {
          age[i] = -1;
          write(i, 0, 0, 0);
          continue;
        }
        const a = tail < 0 ? 0 : tail;
        const b = head > l ? l : head;
        // Fade only over the final `trail` metres, as the streak is swallowed.
        write(i, a, b, Math.min(1, (l - a) / trail));
        live++;
      }
      alpha.needsUpdate = true;
      mesh.instanceMatrix.needsUpdate = true;
      mesh.visible = true;
    },
    dispose() {
      mesh.removeFromParent();
      mesh.dispose();
      geo.dispose();
      mat.dispose();
    },
  };
}
